import type { ApiResponse } from "@dm-online/shared";
import { useAuthStore } from "@/stores/authStore";
import { handleLocalDecks, isLocalDeckPath } from "@/lib/localDecks";

const BASE = `${import.meta.env.VITE_API_BASE ?? ""}/api`;

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  // ゲストのデッキはサーバーに置かず、ブラウザ内に保存する
  if (useAuthStore.getState().user?.id.startsWith("guest-") && isLocalDeckPath(path)) {
    const body = options.body ? JSON.parse(options.body as string) : {};
    return handleLocalDecks<T>(path, options.method ?? "GET", body);
  }
  const token = useAuthStore.getState().token;
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  // 通信・パース失敗でも例外を投げず ApiResponse を返す
  // （呼び出し側が catch しておらず、送信中のまま固まるのを防ぐ）
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...options, headers });
  } catch {
    return { ok: false, error: "通信に失敗しました" };
  }

  // サーバーエラー時に本文がJSONでない（プレーンテキストの500など）場合もある
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const errorMessage = (body as { error?: string } | null)?.error;

  // トークン期限切れなど認証エラー時は再ログインへ誘導する
  if (res.status === 401) {
    const wasGuest = useAuthStore.getState().user?.id.startsWith("guest-");
    useAuthStore.getState().clearAuth();
    // ゲストは再読み込みで自動的に新しいゲストとして入り直す
    window.location.href = wasGuest ? "/" : "/login?expired=1";
    return { ok: false, error: errorMessage ?? "認証の有効期限が切れました" };
  }

  if (!res.ok) {
    return { ok: false, error: errorMessage ?? `エラーが発生しました (${res.status})` };
  }

  return { ok: true, data: body as T };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
